import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Menu, TrendingUp, Globe, Heart, Trophy } from 'lucide-react';
import HoverMenu from './HoverMenu';
import WalletConnect from './WalletConnect';
import NotificationDropdown from './NotificationDropdown';
import { useAuth } from '../contexts/AuthContext';
import { useDisconnect } from 'wagmi';
import { useMessage } from '../contexts/MessageContext';

function Header({ activeFilter, onFilterChange, showFilters = true }) {
  const { isLoggedIn, user } = useAuth();
  const { disconnect } = useDisconnect();
  const { warning, info } = useMessage();
  
  const handleDisconnect = () => {
    // 显示确认对话框
    if (window.confirm('Are you sure you want to disconnect your wallet?')) {
      console.log('🚺 Disconnecting wallet');
      disconnect();
      // 注意：不在这里显示成功消息，因为 AuthContext 会处理
    } else {
      info('Disconnect cancelled');
    }
  };
  
  // console.log('📱 Header 渲染, isLoggedIn:', isLoggedIn, 'user:', user);
  return (
    <header className="bg-plypto-bg border-b border-white/10">
      {/* 主导航栏 */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="h-16 flex items-center justify-between">
          {/* 左侧 Logo */}
          <div className="flex items-center space-x-3" style={{width:'60%'}}>
            <Link to="/" className="font-bold text-xl tracking-wide text-white hover:text-blue-400 transition-colors cursor-pointer">
              Plypto
            </Link>
            <Globe className="w-5 h-5 text-blue-400" />
            <div className="relative" style={{width:'60%'}}>
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search cryptos"
                className="w-full bg-white/10 border border-white/20 rounded-lg pl-10 pr-12 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-plypto-primary"
              />
              <div className="absolute right-3 top-2.5 text-gray-400">
                <span className="text-sm">/</span>
              </div>
            </div>
          </div>

          {/* 右侧按钮 */}
          <div className="flex items-center space-x-5">
            {/* 钱包连接组件 - 始终显示，组件内部处理不同状态 */}
            
            
            {isLoggedIn && (
              // 已认证状态：显示额外功能
              <div className="flex items-center space-x-5">
                
                {/* Portfolio */}
                <div className="flex flex-col items-start">
                  <span className="text-gray-400 text-xs">Plyp Coins</span>
                  <span className="text-green-400 font-semibold text-sm">$0.00</span>
                </div>

                {/* Notifications */}
                <NotificationDropdown />
              </div>
            )}
            
            <WalletConnect />
            
            {/* 排行榜入口 */}
            <Link 
              to="/leaderboard"
              className="flex items-center space-x-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors group"
              title="Prediction Leaderboard"
            >
              <Trophy className="w-5 h-5 text-yellow-400 group-hover:text-yellow-300" />
              <span className="text-sm font-medium hidden sm:block">Leaderboard</span>
            </Link>
            
            <HoverMenu />
          </div>
        </div>

        {/* 导航标签栏 - 三层分类结构 */}
        {showFilters && (
        <div className="py-3 border-t border-white/5 overflow-x-auto">
          <div className="flex items-center space-x-6 min-w-max px-1">
            {/* 第一类：用户个性化 */}
            <div className="flex items-center space-x-1">
              <button 
                onClick={() => onFilterChange('Active')}
                className={`flex items-center space-x-1 text-sm px-3 py-1.5 rounded-md transition-all ${
                  activeFilter === 'Active' 
                    ? 'bg-white/15 text-white font-bold' 
                    : 'text-white hover:bg-white/10 font-medium'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Active</span>
              </button>
              <button 
                onClick={() => onFilterChange('Watchlist')}
                className={`flex items-center space-x-1 text-sm px-3 py-1.5 rounded-md transition-all ${
                  activeFilter === 'Watchlist' 
                    ? 'bg-white/15 text-white font-bold' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5 font-medium'
                }`}
              >
                <Heart className="w-4 h-4" />
                <span>Watchlist</span>
              </button>
            </div>
            
            {/* 分隔符 */}
            <span className="text-gray-600 text-sm">|</span>
            
            {/* 第二类：币种分类 */}
            <div className="flex items-center space-x-1">
              <button 
                onClick={() => onFilterChange('Major')}
                className={`text-sm px-3 py-1.5 rounded-md transition-all ${
                  activeFilter === 'Major' 
                    ? 'bg-white/15 text-white font-bold' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5 font-medium'
                }`}
              >
                Major
              </button>
              <button 
                onClick={() => onFilterChange('New')}
                className={`text-sm px-3 py-1.5 rounded-md transition-all ${
                  activeFilter === 'New' 
                    ? 'bg-white/15 text-white font-bold' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5 font-medium'
                }`}
              >
                New
              </button>
              <button 
                onClick={() => onFilterChange('Altcoins')}
                className={`text-sm px-3 py-1.5 rounded-md transition-all ${
                  activeFilter === 'Altcoins' 
                    ? 'bg-white/15 text-white font-bold' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5 font-medium'
                }`}
              >
                Altcoins
              </button>
            </div>
            
            {/* 分隔符 */}
            <span className="text-gray-600 text-sm">|</span>
            
            {/* 第三类：赛道分类 */}
            <div className="flex items-center space-x-1">
              {['Meme', 'RWA', 'Payments', 'AI', 'Metaverse', 'Seed', 'DeFi'].map(tag => (
                <button 
                  key={tag}
                  onClick={() => onFilterChange(tag)}
                  className={`text-sm px-3 py-1.5 rounded-md transition-all ${
                    activeFilter === tag 
                      ? 'bg-white/15 text-white font-bold' 
                      : 'text-gray-400 hover:text-white hover:bg-white/5 font-medium'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
        )}
      </div>
    </header>
  );
}

export default Header;
